import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CardMedia,
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { useGetBrands } from '../../api/brands';
import type { Brand } from '../../types/brand';
import { useTranslation } from 'react-i18next';
import { getBrandNameFromBrand, getBrandDescriptionFromBrand } from '../../utils/productUtils';

const CARD_WIDTH = 370;
const SKELETON_COUNT = 5;

const BrandSection: React.FC = () => {
  const { brandsData, brandsIsLoading } = useGetBrands();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();

  const scrollByCard = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = CARD_WIDTH + 24; // card width + gap
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  const handleBrandClick = (brand: Brand) => {
    navigate(`/brand/${brand.id}/${encodeURIComponent(getBrandNameFromBrand(brand, i18n.language))}`);
  };

  return (
    <Box sx={{ mt: 6, maxWidth: 'lg', mx: 'auto', width: '100%', px: '15px' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography fontWeight={700} sx={{ ml: 1, fontSize: '18px' }}>
          {t('brands.title')}
        </Typography>
        
        {/* Desktop: Navigation arrows on the right */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              onClick={() => scrollByCard('left')}
              size="small"
              sx={{
                backgroundColor: 'transparent',
                color: '#131518',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                },
              }}
            >
              <ArrowBackIosNew sx={{ fontSize: '20px' }} />
            </IconButton>
            <IconButton
              onClick={() => scrollByCard('right')}
              size="small"
              sx={{
                backgroundColor: 'transparent',
                color: '#131518',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.1)',
                },
              }}
            >
              <ArrowForwardIos sx={{ fontSize: '20px' }} />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box>
        <Box
          ref={scrollContainerRef}
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            pb: 2,
            overflowX: isMobile ? 'visible' : 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            WebkitOverflowScrolling: 'touch',
          }}
        >
        {(brandsIsLoading ? Array.from({ length: SKELETON_COUNT }) : brandsData).map(
          (item, idx) => (
            <Box
              key={brandsIsLoading ? idx : (item as Brand).id}
              onClick={() => !brandsIsLoading && handleBrandClick(item as Brand)}
              sx={{
                minWidth: isMobile ? '42vw' : CARD_WIDTH,
                maxWidth: isMobile ? '42vw' : CARD_WIDTH,
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #E8E8E8',
                borderBottom: `3px solid ${theme.palette.custom.brown}`,
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.2s ease-in-out',
                },
              }}
            >
              {brandsIsLoading ? (
                <Skeleton
                  variant="rectangular"
                  height={200}
                  sx={{
                    width: '100%',
                    objectFit: 'cover',
                    aspectRatio: 1,
                  }}
                />
              ) : (
                <CardMedia
                  component="img"
                  image={(item as Brand).imageUrl || '/images/img_placeholder.svg'}
                  alt={getBrandNameFromBrand(item as Brand, i18n.language)}
                  sx={{
                    width: '100%',
                    objectFit: 'cover',
                    aspectRatio: 1,
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/images/img_placeholder.svg';
                  }}
                />
              )}
              <Box
                sx={{
                  flex: 1,
                  p: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderBottom: `2px solid ${theme.palette.custom.brown}`,
                }}
              >
                {brandsIsLoading ? (
                  <>
                    <Skeleton width="60%" height={28} sx={{ mb: 1 }} />
                    <Skeleton width="80%" height={18} />
                  </>
                ) : (
                  <>
                    <Typography
                      fontWeight={700}
                      align="center"
                      sx={{ mb: 0.5 }}
                    >
                      {getBrandNameFromBrand(item as Brand, i18n.language)}
                    </Typography>
                    <Typography
                      color="text.secondary"
                      align="center"
                      sx={{ 
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        fontSize: '13px',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {getBrandDescriptionFromBrand(item as Brand, i18n.language)}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )
        )}
        </Box>
      </Box>
    </Box>
  );
};

export default BrandSection;
